export type NativeLineADFMarkType = Extract<NativeADFMarkType, 'underline' | 'strike'>;
export type NativeADFMarkType =
  | 'em'
  | 'strong'
  | 'underline'
  | 'strike'
  | 'code'
  | 'link';
export type SearchMatchMarkType = 'match' | 'matchActive' | 'matchText';
export type ADFTextColorMarkType = 'textColor';
export type ADFMarkType =
  | NativeADFMarkType
  | ADFTextColorMarkType
  | PrefixedFabricMarkType
  | SearchMatchMarkType;

export type PackedFabricType = 'markAttributes';
export type PrefixedFabricMarkType = `fabric:${FabricMarkTypes}`;
export type FabricMarkTypes = 'semi-bold' | 'medium';

export interface ADFMark {
  type: ADFMarkType;
  attrs?: Record<string, unknown>;
}

export type ADFNode = ADFBlockNode | ADFInlineNode | ADFHardBreakNode;

export type ADFDocumentContentNode =
  | ADFParagraphNode
  | ADFBulletListNode
  | ADFOrderedListNode
  | ADFListItemNode
  | ADFHeadingNode;

export type ADFDocumentContentNodeTextOnly =
  | ADFParagraphNodeTextOnly
  | ADFHeadingNodeTextOnly
  | ADFBulletListNode
  | ADFOrderedListNode
  | ADFListItemNode;

export type ADFBlockNode = ADFDocument | ADFDocumentContentNode;

export type ADFInlineNode =
  | ADFTextNode
  | ADFMentionNode
  | ADFDateNode
  | ADFEmojiNode
  | ADFInlineSmartCardNode;

export type ADFBlockNodeWithContent = ADFBlockNode &
  Required<Pick<ADFBlockNode, 'content'>>;

export interface ADFDocument {
  type: 'doc';
  id?: string;
  version?: number;
  content: [ADFDocumentContentNode, ...ADFDocumentContentNode[]];
}

export interface ADFTextDocument {
  type: 'doc';
  id?: string;
  version?: number;
  content: [ADFParagraphSingleTextNode];
}

export interface ADFParagraphNode {
  type: 'paragraph';
  attrs?: {
    localId?: string;
  };
  content?: (ADFHardBreakNode | ADFInlineNode)[];
  marks?: ADFMark[];
}

export interface ADFParagraphNodeTextOnly {
  type: 'paragraph';
  attrs?: {
    localId?: string;
  };
  content?: ADFTextNode[];
  marks?: ADFMark[];
}

export interface ADFParagraphSingleTextNode {
  type: 'paragraph';
  attrs?: {
    localId?: string;
  };
  content: [ADFTextNode];
  marks?: ADFMark[];
}

export interface ADFBulletListNode {
  type: 'bulletList';
  attrs?: {
    localId?: string;
  };
  content?: ADFListItemNode[];
}

export interface ADFOrderedListNode {
  type: 'orderedList';
  content?: ADFListItemNode[];
  attrs?: {
    order?: number;
    localId?: string;
  };
}

export type ADFListKind = 'unordered' | 'ordered';

export interface ADFListItemNode {
  type: 'listItem';
  attrs?: {
    localId?: string;
  };
  content?: (ADFParagraphNode | ADFBulletListNode | ADFOrderedListNode)[];
}

export interface ADFTextNode {
  type: 'text';
  text: string;
  marks?: ADFMark[];
}

export interface ADFInlineSmartCardNode {
  type: 'inlineCard';
  attrs: {
    url: string;
    localId?: string;
  };
}

export interface ADFMentionNode {
  type: 'mention';
  attrs: {
    id: string;
    localId?: string;
    text: string;
    accessLevel?: string;
    userType?: 'DEFAULT' | 'SPECIAL' | 'APP' | undefined;
  };
}

export interface ADFDateNode {
  type: 'date';
  attrs: {
    timestamp: string;
    localId?: string;
  };
}

export interface ADFEmojiNode {
  type: 'emoji';
  attrs: {
    shortName: string;
    localId?: string;
    text: string;
  };
}

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface ADFHeadingNode {
  type: 'heading';
  content: (ADFTextNode | ADFEmojiNode | ADFMentionNode | ADFHardBreakNode)[];
  attrs: {
    level: HeadingLevel;
    localId?: string;
  };
}

export interface ADFHeadingNodeTextOnly {
  type: 'heading';
  content: ADFTextNode[];
  attrs: {
    level: HeadingLevel;
    localId?: string;
  };
}

export interface ADFHardBreakNode {
  type: 'hardBreak';
  attrs?: {
    localId?: string;
  };
}

export type WithContent<T extends { content?: any }> = T & Required<Pick<T, 'content'>>;

export type TextAlignment = 'center' | 'left' | 'right';
export enum VerticalTextAlignment {
  TOP,
  MIDDLE,
  BOTTOM,
}

export const singleSpaceADFContent: ADFDocument = {
  version: 1,
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [
        {
          type: 'text',
          text: ' ',
        },
      ],
    },
  ],
};

export const emptyADFContent: ADFDocument = {
  version: 1,
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [],
    },
  ],
};
